// Offline queue (M15, D-140): a note written with no signal is kept on this device and sent, in order,
// as soon as the connection is back. Only actions that are safe to send late and in order are queued (a
// note on a lead); everything else still says it needs a connection. Entries belong to the member who
// wrote them: another member signing in on the device never sends them, and signing out drops them.
import { useSyncExternalStore } from 'react';
import { api, ApiError } from './api.js';

export interface QueuedAction {
  id: string;
  memberId: string;
  method: 'POST';
  path: string;
  body: unknown;
  /** What it was, for the "waiting to send" list. */
  label: string;
  createdAt: string;
}

const KEY = 'tatra.offline-queue';
const listeners = new Set<() => void>();
let cache: QueuedAction[] | null = null;

function read(): QueuedAction[] {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    cache = raw ? (JSON.parse(raw) as QueuedAction[]) : [];
  } catch {
    cache = [];
  }
  return cache;
}

function write(next: QueuedAction[]): void {
  cache = next;
  try {
    if (next.length) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    // storage full or blocked: the queue still lives for this tab
  }
  listeners.forEach((l) => l());
}

/** A request that never reached the server (no network), as opposed to one the server refused. */
export function isNetworkError(e: unknown): boolean {
  return !(e instanceof ApiError) && (e instanceof TypeError || !navigator.onLine);
}

/**
 * Sends now, or — when there is no connection — keeps it for later. Resolves `'sent'` with the server's
 * answer or `'queued'`; a refusal from the server is thrown as usual.
 */
export async function sendOrQueue<T>(
  memberId: string,
  action: { path: string; body: unknown; label: string },
): Promise<{ status: 'sent'; result: T } | { status: 'queued' }> {
  if (navigator.onLine) {
    try {
      return { status: 'sent', result: await api<T>('POST', action.path, action.body) };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  write([
    ...read(),
    {
      id: crypto.randomUUID(),
      memberId,
      method: 'POST',
      path: action.path,
      body: action.body,
      label: action.label,
      createdAt: new Date().toISOString(),
    },
  ]);
  return { status: 'queued' };
}

let replaying: Promise<{ sent: number; dropped: QueuedAction[] }> | null = null;

/**
 * Sends the member's waiting actions in the order they were made. Stops at the first network failure
 * (the rest wait for the next try); an action the server refuses is dropped and reported.
 */
export function replay(memberId: string): Promise<{ sent: number; dropped: QueuedAction[] }> {
  replaying ??= (async () => {
    let sent = 0;
    const dropped: QueuedAction[] = [];
    try {
      for (const a of read().filter((x) => x.memberId === memberId)) {
        try {
          await api(a.method, a.path, a.body);
          sent += 1;
        } catch (e) {
          if (isNetworkError(e)) break;
          dropped.push(a);
        }
        write(read().filter((x) => x.id !== a.id));
      }
    } finally {
      replaying = null;
    }
    return { sent, dropped };
  })();
  return replaying;
}

/** Forgets everything waiting (signing out on a shared phone). */
export function clearQueue(): void {
  write([]);
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) {
      cache = null;
      l();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

/** The member's waiting actions, live. */
export function useQueued(memberId: string | undefined): QueuedAction[] {
  const all = useSyncExternalStore(subscribe, read, () => [] as QueuedAction[]);
  return memberId ? all.filter((a) => a.memberId === memberId) : [];
}

/** Whether the browser thinks it is online, live. */
export function useOnline(): boolean {
  return useSyncExternalStore(
    (l) => {
      window.addEventListener('online', l);
      window.addEventListener('offline', l);
      return () => {
        window.removeEventListener('online', l);
        window.removeEventListener('offline', l);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}
